import type { BodyChange, HandoverDetail, HandoverPhoto, HandoverVersion } from '@acasa/shared'

/*
 * Small helpers around Fassungen that only the editor needs. Kept here rather
 * than in `@acasa/shared` because they decide nothing that ends up in the
 * document — the server owns which photos are frozen (DELETE answers 409) and
 * what a change list contains; these only spare the user a button that would
 * be refused.
 */

export function latestVersion(protocol: HandoverDetail): HandoverVersion | null {
  return protocol.versions.at(-1) ?? null
}

/**
 * The Fassung a photo belongs to, or null when it was taken during the change
 * under way. A photo uploaded before a version was finalised is part of that
 * version's PDF; deleting it would rewrite a signed original. Mirrors the
 * server's rule (`createdAt <= finalisedAt` of the latest version).
 */
export function frozenIn(protocol: HandoverDetail, photo: HandoverPhoto): number | null {
  if (protocol.status !== 'amending') return null
  const latest = latestVersion(protocol)
  if (!latest) return null
  if (Date.parse(photo.createdAt) > Date.parse(latest.finalisedAt)) return null

  // The earliest version finalised after the photo was taken is the one it
  // first appeared in — that is the number worth printing in the tooltip.
  const first = protocol.versions.find(
    (version) => Date.parse(photo.createdAt) <= Date.parse(version.finalisedAt),
  )
  return first?.version ?? latest.version
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * A change as a reader sees it. A null side is the item not existing there —
 * a meter added, a party removed — and reads as such; an empty string is a
 * field that was blank, which prints as a dash like everywhere else in the
 * protocol. The two must not look alike: "removed" and "cleared" are
 * different statements about a signed record.
 */
export function describeChange(change: BodyChange): {
  before: string
  after: string
  /** "hinzugefügt" / "entfernt", or null for an ordinary edit. */
  status: string | null
} {
  const show = (value: string | null) => (value === null || value === '' ? '—' : value)

  if (change.before === null) {
    return { before: '—', after: show(change.after), status: 'hinzugefügt' }
  }
  if (change.after === null) {
    return { before: show(change.before), after: 'entfernt', status: 'entfernt' }
  }
  return { before: show(change.before), after: show(change.after), status: null }
}
